/**
 * BillingNoticesDrawer — billing emails sent to an agency's admins + related Stripe events.
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { Badge } from "@/components/ui/badge";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Loader2, Mail, MailX, Webhook } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
export const NOTICE_LABEL = {
  payment_failed: { label: "Payment failed", cls: "bg-amber-50 text-amber-800 border-amber-200" },
  access_paused: { label: "Access paused", cls: "bg-rose-50 text-rose-700 border-rose-200" },
  cancelled: { label: "Cancelled", cls: "bg-slate-100 text-slate-600 border-slate-200" },
  access_restored: { label: "Access restored", cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
};
const fmt = (d) => (d ? new Date(d).toLocaleString() : "—");

export default function BillingNoticesDrawer({ agency, onClose }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    if (!agency) return;
    setData(null);
    axios.get(`${API}/platform/agencies/${agency.id}/billing-notices`).then((r) => setData(r.data)).catch(() => setData({ notices: [], events: [], subscription: {} }));
  }, [agency]);

  const sub = data?.subscription || {};
  return (
    <Sheet open={!!agency} onOpenChange={(v) => !v && onClose()}>
      <SheetContent className="w-[480px] sm:max-w-[480px] overflow-y-auto" data-testid="billing-notices-drawer">
        <SheetHeader><SheetTitle>Billing notices · {agency?.name}</SheetTitle></SheetHeader>
        {!data ? <Loader2 className="w-5 h-5 animate-spin text-slate-400 mt-6" /> : (
          <div className="mt-4 space-y-6 text-sm">
            <div className="rounded-md border border-slate-200 p-3 text-xs text-slate-600 grid grid-cols-2 gap-y-1" data-testid="billing-notices-subscription">
              <span>Status</span><span className="font-medium text-slate-900 capitalize">{sub.status || "—"}{sub.access_paused ? " · paused" : ""}</span>
              <span>Plan</span><span className="font-medium text-slate-900 capitalize">{sub.plan_id || "—"}</span>
              <span>Payment</span><span className="font-medium text-slate-900 capitalize">{sub.payment_status || "—"}</span>
              <span>First failure</span><span>{fmt(sub.payment_failed_at)}</span>
              <span>Paused at</span><span>{fmt(sub.access_paused_at)}</span>
              <span>Stripe customer</span><span className="font-mono truncate">{sub.stripe_customer_id || "—"}</span>
            </div>

            <section>
              <h4 className="font-semibold text-slate-900 mb-2 flex items-center gap-2"><Mail className="w-4 h-4 text-teal-600" /> Emails sent ({data.notices.length})</h4>
              {data.notices.length === 0 ? <p className="text-xs text-slate-500 italic" data-testid="billing-notices-empty">No billing emails have been sent to this agency.</p> : (
                <ul className="space-y-2">
                  {data.notices.map((n, i) => {
                    const meta = NOTICE_LABEL[n.kind] || { label: n.kind, cls: "" };
                    const failed = (n.recipients || []).filter((r) => !(n.sent || []).includes(r));
                    return (
                      <li key={`${n.at}-${i}`} className="rounded-md border border-slate-200 p-2.5" data-testid={`billing-notice-${i}`}>
                        <div className="flex items-center gap-2">
                          <Badge variant="outline" className={meta.cls}>{meta.label}</Badge>
                          <span className="text-xs text-slate-500 ml-auto">{fmt(n.at)}</span>
                        </div>
                        <p className="text-xs text-slate-600 mt-1.5">
                          {(n.sent || []).length > 0 && <span className="text-emerald-700"><Mail className="w-3 h-3 inline mr-1" />Sent to {n.sent.join(", ")}</span>}
                          {failed.length > 0 && <span className="text-rose-600 block"><MailX className="w-3 h-3 inline mr-1" />Not delivered: {failed.join(", ")}</span>}
                          {(n.recipients || []).length === 0 && <span className="text-amber-700">No admin email on file</span>}
                        </p>
                      </li>
                    );
                  })}
                </ul>
              )}
            </section>

            <section>
              <h4 className="font-semibold text-slate-900 mb-2 flex items-center gap-2"><Webhook className="w-4 h-4 text-slate-500" /> Stripe events ({data.events.length})</h4>
              {data.events.length === 0 ? <p className="text-xs text-slate-500 italic">No Stripe events recorded for this customer.</p> : (
                <ul className="space-y-0.5">
                  {data.events.map((ev, i) => (
                    <li key={ev.id || i} className="text-xs text-slate-600 flex gap-2"><span className="text-slate-400 whitespace-nowrap">{fmt(ev.received_at)}</span><code className="truncate">{ev.type}</code><span className={ev.result === "handled" ? "text-emerald-700 ml-auto" : "text-slate-400 ml-auto"}>{ev.result}</span></li>
                  ))}
                </ul>
              )}
            </section>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
